import { useCallback, useEffect, useState } from 'react';
import { Box, Button, Container, IconButton, Tooltip } from '@mui/material';
import ORMDataGridV2 from '../../Shared/Table/ORMDataGridV2';
import { TRIGGER_TITLE_RESULT_PARENT_COLUMNS } from 'src/constants/dataGridColumns';
import APIService from 'src/utils/APIService';
import RenderSpinner from '../../Spinner';
import Heading from '../../Shared/UI/Heading';
import { Download, Visibility } from '@mui/icons-material';
import { downloadCSV } from 'src/utils/CsvDownloadHelper';
import ormToast, { TOAST_TYPE } from 'src/utils/ormToast';
import TriggerTitlesChildTable from './TriggerTitlesChildTable';
import TriggerTitleResultsModal from './TriggerTitleResultsModal';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import ORMFilters from 'src/components/Shared/Filters/ORMFilters';
import { FormatDateString } from 'src/utils/DateTimeHelper';

const TriggerTitlesResult = () => {
  const [triggerResults, setTriggerResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRows, setSelectedRows] = useState([]);
  const [showTriggerResultsModal, setShowTriggerResultsModal] = useState(false);
  const [viewTriggerResultId, setViewTriggerResultId] = useState(null);
  const [detailPanelExpandedRowIds, setDetailPanelExpandedRowIds] = useState([]);
  const apiRef = useGridApiRef();
  // Default range = last 3 months
  const defaultEndDate = FormatDateString(new Date());
  const defaultStartDate = FormatDateString(new Date(new Date().setMonth(new Date().getMonth() - 1)));

  useEffect(() => {
    if (apiRef.current) {
      apiRef.current.resetRowHeights(); // force recalculation
    }
  }, [detailPanelExpandedRowIds]);

  // Fetch data with optional date params
  const fetchData = useCallback(async (startDate, endDate) => {
    setLoading(true);
    try {
      const params = startDate && endDate ? { startDate, endDate } : {};
      const response = await APIService.get('/triggertitle/result', { params });

      if (response.status === 200) {
        setTriggerResults(response.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch on page load & when date range changes
  useEffect(() => {
    fetchData(defaultStartDate, defaultEndDate);
    document.title = 'View Trigger Result - Peggy';
  }, [fetchData]);

  const getDetailPanelContent = ({ row }) => {
    if (!row.titlesUpdatedInCampaign) return undefined;
    if (detailPanelExpandedRowIds?.includes(row._id)) {
      return <TriggerTitlesChildTable id={row._id} />;
    } else {
      return <></>;
    }
  };

  const handleCSVExport = async () => {
    if (!selectedRows || selectedRows?.length === 0) {
      ormToast('Please Select Row!', TOAST_TYPE.ERROR);
      return;
    }
    setLoading(true);
    const ids = selectedRows.map(value => value._id);
    try {
      const response = await APIService.post('/triggertitle/result/export', { ids });
      if (response.status === 200) {
        //Download all titles in csv format
        let filename = 'Trigger Title Result.csv';
        downloadCSV(response.data, filename);
        setLoading(false);
      } else {
        ormToast(response.data?.message, TOAST_TYPE.ERROR);
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
    }
  };

  const viewTriggerResults = data => {
    setShowTriggerResultsModal(true);
    setViewTriggerResultId(data?._id);
  };

  const columns = [
    ...TRIGGER_TITLE_RESULT_PARENT_COLUMNS,
    {
      field: '_id',
      headerName: 'Actions',
      type: 'actions',
      width: 130,
      getActions: ({ row }) => {
        return [
          <Tooltip title='View trigger result' key='view'>
            <IconButton size='small' onClick={() => viewTriggerResults(row)}>
              <Visibility color='success' fontSize='small' />
            </IconButton>
          </Tooltip>,
        ];
      },
    },
  ];

  return (
    <Container>
      {loading && <RenderSpinner message='Loading....' overlay={true} OverlayTrigger={true} />}
      <Heading heading='View Trigger Result' />
      <Box
        sx={{
          mb: 2,
        }}
      >
        <ORMFilters
          handleSubmit={data => fetchData(data.startDate, data.endDate)}
          defaultStartDate={defaultStartDate}
          defaultEndDate={defaultEndDate}
        />
      </Box>
      <ORMDataGridV2
        apiRef={apiRef}
        columns={columns}
        rows={triggerResults}
        getDetailPanelContent={getDetailPanelContent}
        checkboxSelection
        onRowSelectionChange={setSelectedRows}
        toolbarOptions={['COLUMNS', 'FILTER', 'DENSITY', 'SEARCH']}
        detailPanelExpandedRowIds={detailPanelExpandedRowIds || []}
        getDetailPanelHeight={() => 'auto'}
        onDetailPanelExpandedRowIdsChange={newIds => {
          setDetailPanelExpandedRowIds(Array.from(newIds));
        }}
        pagination
      >
        <Button size='small' sx={{ ml: 1 }} onClick={handleCSVExport}>
          <Download sx={{ mr: 1 }} />
          Export Titles
        </Button>
      </ORMDataGridV2>
      {showTriggerResultsModal && (
        <TriggerTitleResultsModal
          show={showTriggerResultsModal}
          id={viewTriggerResultId}
          handleClose={() => setShowTriggerResultsModal(false)}
        />
      )}
    </Container>
  );
};

export default TriggerTitlesResult;
